import { match } from 'ts-pattern'
import { type FixCounts, type ScaCounts, type Tool, type TriageCounts } from '../../utils/api-client/api-schemas'
import { toolColors } from '../../utils/color-utils'
import { getFixableFindingsCount } from '../../utils/finding-statistics'
import {
  type FilterableClassification,
  type FilterableFixStatus,
  type FilterStateStore,
  type FilterStateStoreValues,
} from '../tables/filter-state-store'
import { type FindingQuickViewId } from '../tables/finding-quick-view-config'
import { type SegmentProps } from './segments-bar'
import { type SegmentLabelProps } from './segments-labels'

export type TriageSegment =
  'True positive' | 'Likely true positive' | 'Inconclusive' | 'False positive' | "Won't fix" | 'Not triaged'

export type FixSegment = 'High confidence' | 'Medium confidence' | 'Low confidence' | 'No rating' | 'No fix'

export type ScaSegment = 'Exploitable' | 'Not exploitable' | 'Potentially exploitable' | 'Not researched'

export type CompositionBarSegments = {
  triage?: TriageSegment[]
  fix?: FixSegment[]
  sca?: ScaSegment[]
}

type SegmentDefinition<T extends string = string> = {
  label: T
  value: number
  alpha: number
  variant?: 'dashed'
  subLabel?: string
}

const colorMix = (color: string, alpha: number): string =>
  `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`

const toSegments = <T extends string>(
  definitions: SegmentDefinition<T>[],
  tool: Tool,
  total: number
): SegmentProps<T>[] => {
  const baseColor = toolColors[tool].backgroundColor

  return definitions.map(def => ({
    ariaLabel: def.label,
    percentage: total > 0 ? (def.value / total) * 100 : 0,
    color: colorMix(baseColor, def.alpha),
    variant: def.variant,
  }))
}

const toLabels = <T extends string>(
  definitions: SegmentDefinition<T>[],
  tool: Tool,
  total: number
): SegmentLabelProps<T>[] => {
  const baseColor = toolColors[tool].backgroundColor

  return definitions.map(def => ({
    name: def.label,
    percentage: total > 0 ? (def.value / total) * 100 : 0,
    subLabel: `${def.value} findings${def.subLabel ? ` ${def.subLabel}` : ''}`,
    color: colorMix(baseColor, def.alpha),
  }))
}

// Definitive triage outcomes; `Not triaged` is the remainder (total - these), which absorbs
// findings still in blocked/excluded/failed/no_recommendations/not_triaged statuses.
const getTriageOutcomeCounts = (counts: TriageCounts) => {
  const { true_positive, likely_true_positive, inconclusive, false_positive, wont_fix } = counts.outcome
  const triagedCount = true_positive + likely_true_positive + inconclusive + false_positive + wont_fix
  return { true_positive, likely_true_positive, inconclusive, false_positive, wont_fix, triagedCount }
}

export const mapTriageToSegments = (counts: TriageCounts, total: number, tool: Tool): SegmentProps<TriageSegment>[] => {
  const { true_positive, likely_true_positive, inconclusive, false_positive, wont_fix, triagedCount } =
    getTriageOutcomeCounts(counts)
  const notTriaged = total - triagedCount

  const definitions: SegmentDefinition<TriageSegment>[] = [
    { label: 'True positive', value: true_positive, alpha: 1 },
    { label: 'Likely true positive', value: likely_true_positive, alpha: 0.8 },
    { label: 'Inconclusive', value: inconclusive, alpha: 0.6 },
    { label: 'False positive', value: false_positive, alpha: 0.4 },
    { label: "Won't fix", value: wont_fix, alpha: 0.4 },
    { label: 'Not triaged', value: notTriaged, alpha: 0, variant: 'dashed' },
  ]

  return toSegments(definitions, tool, total)
}

export const mapTriageToLabels = (
  counts: TriageCounts,
  total: number,
  tool: Tool
): SegmentLabelProps<TriageSegment>[] => {
  const { true_positive, likely_true_positive, inconclusive, false_positive, wont_fix, triagedCount } =
    getTriageOutcomeCounts(counts)
  const notTriaged = total - triagedCount

  const definitions: SegmentDefinition<TriageSegment>[] = [
    { label: 'True positive', value: true_positive, alpha: 1, subLabel: 'validated' },
    { label: 'Likely true positive', value: likely_true_positive, alpha: 0.8, subLabel: 'suspicious' },
    { label: 'Inconclusive', value: inconclusive, alpha: 0.6 },
    { label: 'False positive', value: false_positive, alpha: 0.4, subLabel: 'dismissed' },
    { label: "Won't fix", value: wont_fix, alpha: 0.4, subLabel: 'negligible' },
    { label: 'Not triaged', value: notTriaged, alpha: 0 },
  ]

  return toLabels(definitions, tool, total)
}

// #region Filter linking utilities

const TRIAGE_SEGMENT_TO_FILTER_MAP: Partial<Record<TriageSegment, FilterableClassification>> = {
  'True positive': 'true_positive',
  'Likely true positive': 'suspicious',
  Inconclusive: 'inconclusive',
  'False positive': 'false_positive',
  "Won't fix": 'wont_fix',
}

const NOT_TRIAGED_FILTER_KEYS: FilterableClassification[] = ['not_triaged', 'blocked', 'excluded', 'error']
const NO_FIX_FILTER_KEYS: FilterableFixStatus[] = ['blocked', 'excluded', 'failed']

const FIX_SEGMENT_TO_FILTER_MAP: Partial<Record<FixSegment, string>> = {
  'High confidence': 'high',
  'Medium confidence': 'medium',
  'Low confidence': 'low',
  'No rating': 'no-rating',
}

const SCA_SEGMENT_TO_FILTER_MAP: Partial<Record<ScaSegment, keyof FilterStateStoreValues['scaClassification']>> = {
  Exploitable: 'exploitable',
  'Not exploitable': 'not-exploitable',
  'Potentially exploitable': 'potentially-exploitable',
}

function* triageSelectedSegments(filterState: Pick<FilterStateStore, 'classification'>): Generator<TriageSegment> {
  if (filterState.classification.true_positive) yield 'True positive'
  if (filterState.classification.suspicious) yield 'Likely true positive'
  if (filterState.classification.false_positive) yield 'False positive'
  if (filterState.classification.wont_fix) yield "Won't fix"
  if (filterState.classification.inconclusive) yield 'Inconclusive'
  if (NOT_TRIAGED_FILTER_KEYS.some(key => filterState.classification[key])) yield 'Not triaged'
}

export const getTriageSelectedSegments = (filterState: Pick<FilterStateStore, 'classification'>): TriageSegment[] => [
  ...triageSelectedSegments(filterState),
]

function* fixSelectedSegments(
  filterState: Pick<FilterStateStore, 'fixConfidence' | 'fixResult'>
): Generator<FixSegment> {
  if (filterState.fixConfidence.high) yield 'High confidence'
  if (filterState.fixConfidence.medium) yield 'Medium confidence'
  if (filterState.fixConfidence.low) yield 'Low confidence'
  if (filterState.fixConfidence['no-rating']) yield 'No rating'
  if (NO_FIX_FILTER_KEYS.some(key => filterState.fixResult[key])) yield 'No fix'
}

export const getFixSelectedSegments = (
  filterState: Pick<FilterStateStore, 'fixConfidence' | 'fixResult'>
): FixSegment[] => [...fixSelectedSegments(filterState)]

function* scaSelectedSegments(filterState: Pick<FilterStateStore, 'scaClassification'>): Generator<ScaSegment> {
  if (filterState.scaClassification.exploitable) yield 'Exploitable'
  if (filterState.scaClassification['not-exploitable']) yield 'Not exploitable'
  if (filterState.scaClassification['potentially-exploitable']) yield 'Potentially exploitable'
}

export const getScaSelectedSegments = (filterState: Pick<FilterStateStore, 'scaClassification'>): ScaSegment[] => [
  ...scaSelectedSegments(filterState),
]

export const handleTriageSegmentClick = (
  label: TriageSegment,
  filterState: Pick<FilterStateStore, 'toggleClassification' | 'classification'>
): void => {
  if (label === 'Not triaged') {
    const allActive = NOT_TRIAGED_FILTER_KEYS.every(key => filterState.classification[key])
    for (const key of NOT_TRIAGED_FILTER_KEYS) {
      if (allActive || !filterState.classification[key]) {
        filterState.toggleClassification(key)
      }
    }
    return
  }
  const filterKey = TRIAGE_SEGMENT_TO_FILTER_MAP[label]
  if (filterKey) filterState.toggleClassification(filterKey)
}

export const handleFixSegmentClick = (
  label: FixSegment,
  filterState: Pick<FilterStateStore, 'toggleFixConfidence' | 'toggleFixResult' | 'fixResult'>
): void => {
  if (label === 'No fix') {
    const allActive = NO_FIX_FILTER_KEYS.every(key => filterState.fixResult[key])
    for (const key of NO_FIX_FILTER_KEYS) {
      if (allActive || !filterState.fixResult[key]) {
        filterState.toggleFixResult(key)
      }
    }
    return
  }
  const filterKey = FIX_SEGMENT_TO_FILTER_MAP[label]
  if (filterKey) filterState.toggleFixConfidence(filterKey)
}

export const handleScaSegmentClick = (
  label: ScaSegment,
  filterState: Pick<FilterStateStore, 'toggleScaClassification'>
): void => {
  const filterKey = SCA_SEGMENT_TO_FILTER_MAP[label]
  if (filterKey) filterState.toggleScaClassification(filterKey)
}

// `No rating` = findings with a fix but no confidence rating (reported directly by the API);
// `No fix` = fixable findings that produced no fix (blocked/excluded/errored). `total` is the
// fixable-findings denominator so proportions match the fix label.
const getFixSegmentCounts = (counts: FixCounts) => {
  const { high, medium, low, no_rating } = counts.confidence
  const availableFixCount = counts.outcome.has_fix
  const fixableFindings = getFixableFindingsCount(counts)
  return {
    high,
    medium,
    low,
    noRating: no_rating,
    noFix: fixableFindings - availableFixCount,
    total: fixableFindings,
  }
}

export const mapFixToSegments = (counts: FixCounts, tool: Tool): SegmentProps<FixSegment>[] => {
  const { high, medium, low, noRating, noFix, total } = getFixSegmentCounts(counts)

  const definitions: SegmentDefinition<FixSegment>[] = [
    { label: 'High confidence', value: high, alpha: 1 },
    { label: 'Medium confidence', value: medium, alpha: 0.8 },
    { label: 'Low confidence', value: low, alpha: 0.6 },
    { label: 'No rating', value: noRating, alpha: 0.4 },
    { label: 'No fix', value: noFix, alpha: 0, variant: 'dashed', subLabel: 'blocked, excluded or errored' },
  ]

  return toSegments(definitions, tool, total)
}

export const mapFixToLabels = (counts: FixCounts, tool: Tool): SegmentLabelProps<FixSegment>[] => {
  const { high, medium, low, noRating, noFix, total } = getFixSegmentCounts(counts)

  const definitions: SegmentDefinition<FixSegment>[] = [
    { label: 'High confidence', value: high, alpha: 1 },
    { label: 'Medium confidence', value: medium, alpha: 0.8 },
    { label: 'Low confidence', value: low, alpha: 0.6 },
    { label: 'No rating', value: noRating, alpha: 0.4 },
    { label: 'No fix', value: noFix, alpha: 0, subLabel: 'blocked, excluded or errored' },
  ]

  return toLabels(definitions, tool, total)
}

export const mapScaToSegments = (counts: ScaCounts, total: number, tool: Tool): SegmentProps<ScaSegment>[] => {
  const { exploitable, not_exploitable, inconclusive } = counts.classification
  const notResearched = total - counts.status.completed

  const definitions: SegmentDefinition<ScaSegment>[] = [
    { label: 'Exploitable', value: exploitable, alpha: 1 },
    { label: 'Not exploitable', value: not_exploitable, alpha: 0.8 },
    { label: 'Potentially exploitable', value: inconclusive, alpha: 0.6 },
    { label: 'Not researched', value: notResearched, alpha: 0.4 },
  ]

  return toSegments(definitions, tool, total)
}

export const mapScaToLabels = (counts: ScaCounts, total: number, tool: Tool): SegmentLabelProps<ScaSegment>[] => {
  const { exploitable, not_exploitable, inconclusive } = counts.classification
  const notResearched = total - counts.status.completed

  const definitions: SegmentDefinition<ScaSegment>[] = [
    { label: 'Exploitable', value: exploitable, alpha: 1 },
    { label: 'Not exploitable', value: not_exploitable, alpha: 0.8 },
    { label: 'Potentially exploitable', value: inconclusive, alpha: 0.6 },
    { label: 'Not researched', value: notResearched, alpha: 0.4 },
  ]

  return toLabels(definitions, tool, total)
}

// #endregion

// #region Quick view computation utilities

type NonClickableSegments = {
  triage: TriageSegment[]
  fix: FixSegment[]
  sca: ScaSegment[]
}

export const getQuickViewNonClickableSegments = (quickViewId: FindingQuickViewId): NonClickableSegments => ({
  triage: match(quickViewId)
    .with('validated', (): TriageSegment[] => ['True positive', 'Likely true positive'])
    .with('dismissable', (): TriageSegment[] => ['False positive', "Won't fix"])
    .with('ready-to-fix', (): TriageSegment[] => ['True positive', 'Likely true positive', 'Inconclusive'])
    .otherwise((): TriageSegment[] => []),
  fix: [],
  sca: match(quickViewId)
    .with('sca-exploitable', (): ScaSegment[] => ['Exploitable', 'Not researched'])
    .with('sca-not-exploitable', (): ScaSegment[] => ['Not exploitable', 'Not researched'])
    .otherwise((): ScaSegment[] => ['Not researched']),
})

export const getQuickViewEmptyMessage = (quickViewId: FindingQuickViewId): string =>
  match(quickViewId)
    .with('dismissable', () => 'No dismissable findings')
    .with('unanalyzed', () => 'All findings have been analyzed')
    .with('sca-unanalyzed', () => 'All findings have been analyzed')
    .with('validated', () => 'No validated findings')
    .with('ready-to-fix', () => 'No findings are ready to fix')
    .with('sca-exploitable', () => 'No exploitable findings')
    .with('sca-not-exploitable', () => 'No not-exploitable findings')
    .otherwise(() => 'No findings in this view')

// #endregion
